const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const getResponseData = (error: unknown): Record<string, unknown> | null => {
  if (!isRecord(error) || !isRecord(error.response)) {
    return null;
  }

  return isRecord(error.response.data) ? error.response.data : null;
};

/**
 * Flatten every field message of a Laravel validation response (422) into a
 * single list. Returns an empty list for any other error shape.
 *
 * Laravel's top-level `message` only names the first failing field and appends
 * "(and N more errors)", so it hides the rest — use this to show them all.
 */
export const getApiValidationMessages = (error: unknown): string[] => {
  const errors = getResponseData(error)?.errors;

  if (!isRecord(errors)) {
    return [];
  }

  return Object.values(errors)
    .flatMap((messages) => (Array.isArray(messages) ? messages : [messages]))
    .filter((message): message is string => typeof message === "string");
};

/**
 * The per-field messages of a Laravel validation response (422), keyed by field
 * name and shaped for `useValidation`'s `bindError`. Returns an empty map for
 * any other error shape, so a caller can assign it unconditionally.
 */
export const getApiValidationErrors = (
  error: unknown,
): Record<string, string[]> => {
  const errors = getResponseData(error)?.errors;

  if (!isRecord(errors)) {
    return {};
  }

  return Object.entries(errors).reduce<Record<string, string[]>>(
    (fields, [field, messages]) => {
      const list = (Array.isArray(messages) ? messages : [messages]).filter(
        (message): message is string => typeof message === "string",
      );

      if (list.length > 0) {
        fields[field] = list;
      }

      return fields;
    },
    {},
  );
};

/**
 * The server-provided error message, or the given fallback when the response
 * carries none.
 */
export const getApiErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  const message = getResponseData(error)?.message;

  return typeof message === "string" && message.length > 0 ? message : fallback;
};

/**
 * Narrows a caught `unknown` to an Error.
 *
 * `notifyStore` is untyped JS that reads an axios error's `response.data.message`, so a
 * caught value has to be narrowed rather than cast away before it is handed over.
 */
export const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error));
